"use client";

import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Zap, Lock } from "lucide-react";
import dynamic from "next/dynamic";
import Script from "next/script";
import { useAuth } from "@/contexts/AuthContext";
import { REQUIRE_ACCELERATOR_FOR_PREMIUM_FEATURES } from "@/lib/feature-flags";

const POCKET_REGISTER_BASE =
  "https://u3.shortink.io/register?utm_campaign=835286&utm_source=affiliate&utm_medium=sr&a=9vDAHKuinZMdbX&al=1758302&ac=app-mnc-pd-in-sb&cid=954055";

function getPocketRegisterUrl(email) {
  const e = email || "";
  return `${POCKET_REGISTER_BASE}&sub_id1=${encodeURIComponent(e)}`;
}
const YouTubePlayer = dynamic(() => import("./plyr/YoutubePlayer"), {
  ssr: false,
});

// Componente para player VTurb Legacy que recarrega corretamente na navegação
function VTurbLegacyPlayer({ vturbId, vturbProjectId }) {
  const containerRef = useRef(null);
  const projectId = vturbProjectId || 'c9ad2a89-1d3d-45e5-8628-2a2ae0a0b34d';

  useEffect(() => {
    if (!containerRef.current) return;

    // Limpa o container
    containerRef.current.innerHTML = `
      <div id="vid_${vturbId}" style="position: relative; width: 100%; padding: 56.25% 0 0;">
        <img id="thumb_${vturbId}" src="https://images.converteai.net/${projectId}/players/${vturbId}/thumbnail.jpg" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; display: block;" alt="thumbnail">
        <div id="backdrop_${vturbId}" style="-webkit-backdrop-filter: blur(5px); backdrop-filter: blur(5px); position: absolute; top: 0; height: 100%; width: 100%;"></div>
      </div>
    `;

    // Remove script antigo se existir
    const oldScript = document.getElementById(`scr_${vturbId}`);
    if (oldScript) {
      oldScript.remove();
    }

    // Carrega o script novo
    const script = document.createElement('script');
    script.id = `scr_${vturbId}`;
    script.src = `https://scripts.converteai.net/${projectId}/players/${vturbId}/player.js`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup ao desmontar
      const scriptToRemove = document.getElementById(`scr_${vturbId}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [vturbId, projectId]);

  return <div ref={containerRef} />;
}

const videos = [
  {
    youtubeId: "y3rKY4MgbSc",
    title: "How AI Works (Start Here)",
    description: "Lesson 01",
  },
  {
    youtubeId: "F2H8NTrvkC8",
    title: "Activate Your Account in 5 Minutes",
    description: "Lesson 02",
    showPocketButton: true,
  },
  {
    youtubeId: "zi9ESMHhhz8",
    title: "Your First AI Trade",
    description: "Lesson 03",
  },
  {
    youtubeId: "O2xyqCMjM9Q",
    title: "Plan $100 → $250,000 in 6 Months",
    description: "Lesson 04",
  },
  {
    youtubeId: "P7FFcQl3hbw",
    title: "Next steps with your AI strategy",
    description: "Lesson 05",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export default function VideoCard() {
  const { buyerInfo, user } = useAuth();

  const hasUsedAccelerator = (buyerInfo?.accelerator_value ?? 0) > 0;
  const lockLessonsWithoutAccelerator =
    REQUIRE_ACCELERATOR_FOR_PREMIUM_FEATURES &&
    !hasUsedAccelerator;

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants}>
      <div className="flex flex-col gap-4">
        {videos.map((video, index) => {
          const isLocked =
            lockLessonsWithoutAccelerator && index >= 2 && index <= 4;
          
          return (
          <div key={video.vturbId || video.youtubeId || index}>
            {/* Subtle divider between lessons */}
            {index > 0 && (
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                <span className="text-xs text-gray-300 font-medium">•</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
              </div>
            )}
            
            {/* Card com bloqueio condicional */}
            <motion.div
              className={`relative bg-white rounded-[16px] shadow-sm border overflow-hidden transition-all ${
                isLocked 
                  ? 'border-gray-200' 
                  : 'border-blue-200 hover:shadow-lg'
              }`}
              variants={itemVariants}
            >
            {/* Video player - com overlay de bloqueio */}
            <div className="relative">
              {/* Overlay de bloqueio sobre o vídeo */}
              {isLocked && (
                <div className="absolute inset-0 z-20 bg-[#0a0f1a]/85 backdrop-blur-[2px] flex flex-col items-center justify-center">
                  <div className="w-14 h-14 bg-[#1a2438] rounded-full flex items-center justify-center shadow-lg border border-white/10 mb-3">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-white text-sm font-medium text-center px-4">
                    Complete Lesson 02 and activate<br />your Pocket to unlock
                  </p>
                </div>
              )}
              
              <div className={isLocked ? 'blur-[2px] pointer-events-none' : ''}>
            {video.vturbId ? (
              <div className="w-full">
                {video.vturbLegacy ? (
                  // Estilo JavaScript antigo (com thumbnail)
                  <VTurbLegacyPlayer 
                    vturbId={video.vturbId} 
                    vturbProjectId={video.vturbProjectId} 
                  />
                ) : (
                  // Estilo smartplayer v4
                  <>
                    <Script 
                      src={`https://scripts.converteai.net/${video.vturbProjectId || 'c9ad2a89-1d3d-45e5-8628-2a2ae0a0b34d'}/players/${video.vturbId}/v4/player.js`}
                      strategy="lazyOnload"
                    />
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `<vturb-smartplayer id="vid-${video.vturbId}" style="display: block; margin: 0 auto; width: 100%;"></vturb-smartplayer>`
                      }}
                    />
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                }}
              >
                {video.youtubeId ? (
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                    <YouTubePlayer videoId={video.youtubeId} forceRefresh={video.forceRefresh} />
                  </div>
                ) : (
                  <iframe
                    src={video.src}
                    className="absolute top-0 left-0 w-full h-full"
                    frameBorder="0"
                    allow="fullscreen"
                    allowFullScreen
                    referrerPolicy="origin"
                  />
                )}
              </div>
            )}
              </div>
            </div>

            {/* Content - sempre visível, sem blur */}
            <div className="p-4" data-clase={video.description.replace('Lesson ', '')}>
              <h2 className="text-base font-semibold text-gray-900">
                {video.title}
              </h2>
              <p className="text-gray-500 text-sm">{video.description}</p>

              {/* CTA Pocket — segunda aula */}
              {video.showPocketButton && (
                <motion.div
                  className="mt-4"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.a
                    className="w-full bg-gradient-to-br from-purple-400 via-violet-500 to-purple-600 text-white font-bold py-3 px-5 rounded-2xl flex items-center justify-center gap-3 shadow-[0_8px_30px_rgba(147,51,234,0.4)] transition-all hover:shadow-[0_12px_40px_rgba(147,51,234,0.5)] border border-white/20"
                    href={getPocketRegisterUrl(user?.email)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <Zap className="w-5 h-5" />
                    </div>
                    <span className="text-base">Go to Pocket</span>
                  </motion.a>
                </motion.div>
              )}
            </div>
            </motion.div>
          </div>
          );
        })}
      </div>
    </motion.div>
  );
}
